<template>
  <Teleport to="body">
    <div v-if="show" class="dir-modal-overlay eh-popup-overlay" @click.self="onCancel">
      <div class="dir-modal eh-popup-panel eh-ds" data-cy="add-directives-modal">
        <!-- Header -->
        <div class="dir-modal-header">
          <div>
            <h3>
              <span class="glyphicon glyphicon-plus bi bi-plus-circle"></span>
              Add New Directives
            </h3>
            <!-- Who this is about, pinned in the header: name · MRN · DOB. -->
            <div v-if="identityLine" class="dir-modal-patient" data-cy="modal-patient">{{ identityLine }}</div>
          </div>
          <button class="dir-close-btn" @click="onCancel" aria-label="Close">
            <span class="glyphicon glyphicon-remove bi bi-x-lg"></span>
          </button>
        </div>

        <!-- Step indicator -->
        <div class="dir-steps">
          <span class="dir-step" :class="{ active: step === 1, done: step > 1 }">1. Input method</span>
          <span class="dir-step-sep">›</span>
          <span class="dir-step" :class="{ active: step === 2 }">2. Attestation</span>
        </div>

        <div class="dir-modal-body">
          <!-- ============================ STEP 1 ============================ -->
          <template v-if="step === 1">
            <p class="dir-section-label">How do you want to add this directive record?</p>
            <div class="dir-method-grid">
              <!-- The Upload card IS the dropzone: drag a file onto it, or
                   click to open the file dialog. Once a valid file lands, the
                   upload-path fields appear below. div, not button — it hosts
                   the remove-file button (nested buttons are invalid HTML). -->
              <div
                class="dir-method-btn dir-method-drop"
                data-cy="method-upload"
                role="button"
                tabindex="0"
                :class="{ active: method === 'upload', 'drag-over': isDragOver }"
                @click="onUploadCardClick"
                @keydown.enter.prevent="onUploadCardClick"
                @dragover.prevent="isDragOver = true"
                @dragleave="isDragOver = false"
                @drop.prevent="handleDrop"
              >
                <!-- Explicit set (no image/* wildcard); HEIC has no reliable MIME
                     type, so it is accepted by extension. `capture` hints the camera.
                     @click.stop: the programmatic .click() bubbles back to the card
                     handler and would loop the dialog open. -->
                <input
                  ref="fileInput"
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png,.heic,.heif"
                  capture="environment"
                  hidden
                  data-cy="file-input"
                  @click.stop
                  @change="handleFileSelect"
                />
                <template v-if="!file">
                  <span class="glyphicon glyphicon-cloud-upload bi bi-cloud-upload"></span>
                  <strong>Upload</strong>
                  <small>A signed document (PDF or photo). The document is the source of truth.</small>
                  <span class="dir-file-types">Drag &amp; drop or click to browse — PDF or image (JPG, PNG, HEIC), max 20MB</span>
                </template>
                <div v-else class="dir-selected-file" data-cy="selected-file">
                  <span class="glyphicon glyphicon-file bi bi-file-earmark-text"></span>
                  <div class="dir-file-details">
                    <span class="dir-file-name">{{ file.name }}</span>
                    <span v-if="converting" class="dir-file-size dir-processing" data-cy="file-processing">
                      <span class="glyphicon glyphicon-refresh dir-spin"></span> Processing image…
                    </span>
                    <span v-else class="dir-file-size">{{ formatFileSize(file.size) }}</span>
                  </div>
                  <button
                    class="dir-remove-file"
                    :disabled="converting"
                    @click.stop="clearFile"
                    aria-label="Remove file"
                  >
                    <span class="glyphicon glyphicon-remove bi bi-x-lg"></span>
                  </button>
                </div>
              </div>
              <button
                class="dir-method-btn"
                data-cy="method-manual"
                :class="{ active: method === 'manual' }"
                @click="selectMethod('manual')"
              >
                <span class="glyphicon glyphicon-edit bi bi-pencil-square"></span>
                <strong>Manual Entry</strong>
                <small>Pick coded directives from the common list. Requires at least one.</small>
              </button>
            </div>

            <!-- ---- Upload sub-form: appears once a valid file is set ---- -->
            <div v-if="method === 'upload'" class="dir-subform">
              <div v-if="fileError" class="dir-inline-error">
                <span class="glyphicon glyphicon-exclamation-sign bi bi-exclamation-triangle"></span>
                {{ fileError }}
              </div>

              <template v-if="file">
                <div class="dir-field">
                  <label>Form label (optional)</label>
                  <input
                    v-model.trim="formLabel"
                    type="text"
                    class="dir-input"
                    placeholder="e.g. MOLST/POLST, Living Will, Health Care Proxy"
                  />
                </div>

                <div class="dir-field">
                  <label>Is this document fully authorized / notarized? *</label>
                  <div class="dir-yesno">
                    <button class="dir-toggle" data-cy="doc-authorized-yes" :class="{ active: documentAuthorized === true }" @click="documentAuthorized = true">Yes</button>
                    <button class="dir-toggle" data-cy="doc-authorized-no" :class="{ active: documentAuthorized === false }" @click="documentAuthorized = false">No</button>
                  </div>
                  <small class="dir-hint">
                    "Yes" enters the record as <strong>Pending Review</strong>; "No" enters it as
                    <strong>Unconfirmed Authorization</strong> until authorization is confirmed.
                  </small>
                </div>
              </template>
            </div>

            <!-- ---- Manual Entry sub-form (shared DirectivePicker) ---- -->
            <div v-if="method === 'manual'" class="dir-subform">
              <DirectivePicker ref="pickerRef" @update:selected="manualDirectives = $event" />
            </div>
          </template>

          <!-- ============================ STEP 2 ============================ -->
          <template v-else>
            <p class="dir-section-label">Who is submitting this on the patient's behalf?</p>

            <div class="dir-field">
              <label for="dir-relationship">Relationship to patient *</label>
              <!-- Native select, not a chip grid: eleven options as buttons ate a
                   whole phone screen, and a native control gets the platform's
                   own picker. -->
              <select
                id="dir-relationship"
                v-model="relationship"
                class="dir-input dir-select"
                data-cy="relationship-select"
              >
                <option value="" disabled>Select a relationship…</option>
                <option v-for="(label, value) in relationshipLabels" :key="value" :value="value">
                  {{ label }}
                </option>
              </select>
            </div>

            <!-- Disclaimer: says WHY "Self" isn't in the list, so its absence
                 reads as a rule rather than a bug. -->
            <small v-if="!submitterIsPatient" class="dir-hint" data-cy="self-hint">
              You are submitting on the patient's behalf — "Self (the patient)" is
              available only when the patient submits their own record.
            </small>

            <div v-if="relationship === 'other'" class="dir-field">
              <label>Please specify *</label>
              <input v-model.trim="relationshipOther" type="text" class="dir-input" placeholder="Describe the relationship" />
            </div>

            <!-- Surrogacy is a third-party question — the patient cannot be
                 their own surrogate, so it disappears when relationship=self
                 (recorded as not-a-surrogate; keyon normalizes the same). -->
            <div v-if="relationship !== 'self'" class="dir-field">
              <label>Is the submitter a legal surrogate? *</label>
              <div class="dir-yesno">
                <button class="dir-toggle" data-cy="surrogate-yes" :class="{ active: isLegalSurrogate === true }" @click="isLegalSurrogate = true">Yes</button>
                <button class="dir-toggle" data-cy="surrogate-no" :class="{ active: isLegalSurrogate === false }" @click="setNotSurrogate">No</button>
              </div>
              <small class="dir-hint">Audit-only. This never bypasses clinician sign-off.</small>
            </div>

            <div v-if="isLegalSurrogate === true" class="dir-field">
              <label for="dir-authority">Authority type *</label>
              <select
                id="dir-authority"
                v-model="authorityType"
                class="dir-input dir-select"
                data-cy="authority-select"
              >
                <option value="" disabled>Select an authority type…</option>
                <option v-for="(label, value) in authorityLabels" :key="value" :value="value">
                  {{ label }}
                </option>
              </select>
            </div>

            <!-- Health care proxy soft warning (proxy-effected directives must be witnessed/notarized). -->
            <div v-if="showProxyWarning" class="dir-soft-warning">
              <span class="glyphicon glyphicon-warning-sign bi bi-exclamation-triangle"></span>
              Directives effected by a Health Care Proxy must be signed, dated, witnessed and
              notarized. Confirm the document meets these requirements before submitting.
            </div>

          </template>
        </div>

        <!-- Footer -->
        <div class="dir-modal-footer">
          <div class="dir-footer-left">
            <button v-if="step === 2" class="dir-btn-secondary" @click="step = 1">Back</button>
          </div>
          <div class="dir-footer-right">
            <!-- Status preview: what this submission will become. -->
            <span v-if="step === 2 && method" class="dir-result-preview">
              Will be created as
              <span class="dir-result-badge">
                <span class="dir-result-dot" :style="{ background: resultStatus.dot }"></span>
                {{ resultStatus.label }}
              </span>
            </span>
            <button class="dir-btn-secondary" data-cy="modal-cancel" @click="onCancel">Cancel</button>
            <button v-if="step === 1" class="dir-btn-primary" data-cy="modal-continue" :disabled="!step1Valid" @click="step = 2">
              Continue
            </button>
            <button v-else class="dir-btn-primary" data-cy="modal-submit" :disabled="!step2Valid || saving" @click="submit">
              <span v-if="saving" class="glyphicon glyphicon-refresh dir-spin"></span>
              {{ saving ? 'Saving…' : 'Submit Directive Record' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import {
  RELATIONSHIP_LABELS,
  RELATIONSHIP_TO_PATIENT,
  AUTHORITY_LABELS,
  AUTHORITY_TYPE,
  DIRECTIVE_STATUS,
  STATUS_LABELS,
  STATUS_DOTS,
} from "@/api/directivesApi";
import DirectivePicker from "./DirectivePicker.vue";
import { formatDate as formatDateValue } from "@/utils/dateOnly";
import {
  convertUploadFile,
  isWithinInputLimit,
  REPICK_MESSAGE,
} from "@/utils/imageConversion";

const props = defineProps({
  show: { type: Boolean, default: false },
  /** Default relationship — 'self' when the logged-in user is the patient. */
  defaultRelationship: { type: String, default: "self" },
  saving: { type: Boolean, default: false },
  patientName: { type: String, default: "" },
  patientDob: { type: String, default: "" },
  /** Preformatted "name · MRN · DOB" header line from the parent page. */
  patientLine: { type: String, default: "" },
  /** True when the logged-in submitter IS the patient. Gates "Self". */
  submitterIsPatient: { type: Boolean, default: false },
});

const emit = defineEmits(["cancel", "submit"]);

const authorityLabels = AUTHORITY_LABELS;

const formattedDob = computed(() => formatDate(props.patientDob));

// Fall back to name (dob) when the parent doesn't pass the full line.
const identityLine = computed(() => {
  if (props.patientLine) return props.patientLine;
  if (!props.patientName) return "";
  return formattedDob.value ? `${props.patientName} (${formattedDob.value})` : props.patientName;
});

// Attestation coherence: "Self (the patient)" is only offered when the
// logged-in submitter IS the patient — "Submitted by Joe Mo · Self" on Jane
// Murray's record is a contradiction the form must not allow. keyon enforces
// the same rule server-side.
const relationshipLabels = computed(() => {
  if (props.submitterIsPatient) return RELATIONSHIP_LABELS;
  const { [RELATIONSHIP_TO_PATIENT.SELF]: _self, ...rest } = RELATIONSHIP_LABELS;
  return rest;
});

function formatDate(d) {
  if (!d) return "";
  return formatDateValue(d, String(d));
}

// ---- step / method ----
const step = ref(1);
const method = ref(""); // 'upload' | 'manual'

// ---- upload state ----
const fileInput = ref(null);
const file = ref(null); // the picked file, kept for name/size display only
// The artifact actually uploaded: images are re-encoded to WebP, PDFs pass
// through. Submit is gated on this existing, so a raw/half-converted file is
// never POSTed. `converting` drives the "Processing…" state.
const convertedFile = ref(null);
const converting = ref(false);
const fileError = ref("");
const isDragOver = ref(false);
const formLabel = ref("");
const documentAuthorized = ref(null); // true | false

// ---- manual state ----
// The catalog + search + multi-select live inside <DirectivePicker>; this just
// holds the directive objects it emits ({ catalog_id, code, label }).
const pickerRef = ref(null);
const manualDirectives = ref([]);

// ---- submitter attestation ----
// An empty defaultRelationship ("") means "force an explicit pick" (clinician
// submitting on behalf); 'self' is pre-selected only when the user is the patient.
const relationship = ref(props.defaultRelationship || "");
const relationshipOther = ref("");
const isLegalSurrogate = ref(null); // true | false
const authorityType = ref("");

// Reset the whole form each time the modal opens.
watch(
  () => props.show,
  (open) => {
    if (open) reset();
  }
);

function reset() {
  step.value = 1;
  method.value = "";
  file.value = null;
  convertedFile.value = null;
  converting.value = false;
  fileError.value = "";
  formLabel.value = "";
  documentAuthorized.value = null;
  manualDirectives.value = [];
  pickerRef.value?.reset();
  relationship.value = props.defaultRelationship || "";
  relationshipOther.value = "";
  // A 'self' default answers the surrogate question implicitly. The watch
  // below only fires on CHANGE — initializing to 'self' would otherwise leave
  // the hidden question unanswered and the submit permanently blocked.
  if (relationship.value === "self") {
    isLegalSurrogate.value = false;
  } else {
    isLegalSurrogate.value = null;
  }
  authorityType.value = "";
}

function selectMethod(m) {
  method.value = m;
}

// The Upload card is the dropzone: clicking it both selects the method and
// opens the file dialog (drop does the same via handleDrop).
function onUploadCardClick() {
  method.value = "upload";
  triggerFileInput();
}

const showProxyWarning = computed(
  () => isLegalSurrogate.value === true && authorityType.value === AUTHORITY_TYPE.HEALTH_CARE_PROXY
);

// Status this submission will be created as (mirrors the backend lifecycle rule):
//   manual            -> pending_review
//   upload, authorized -> pending_review
//   upload, not auth.  -> unconfirmed_authorization
const resultStatus = computed(() => {
  const value =
    method.value === "upload" && documentAuthorized.value !== true
      ? DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION
      : DIRECTIVE_STATUS.PENDING_REVIEW;
  return { label: STATUS_LABELS[value], dot: STATUS_DOTS[value] };
});

function setNotSurrogate() {
  isLegalSurrogate.value = false;
  authorityType.value = "";
}

// Self answers the surrogate question implicitly (the patient is not their own
// surrogate). Leaving self re-opens it as an explicit choice.
watch(relationship, (rel) => {
  if (rel === "self") {
    setNotSurrogate();
  } else {
    isLegalSurrogate.value = null;
    authorityType.value = "";
  }
});

// ---- validation ----
const step1Valid = computed(() => {
  if (method.value === "upload") {
    // Gate on the converted artifact: not while converting, not on a failed pick.
    return !!convertedFile.value && documentAuthorized.value !== null;
  }
  if (method.value === "manual") {
    return manualDirectives.value.length >= 1;
  }
  return false;
});

const step2Valid = computed(() => {
  if (!relationship.value) return false;
  if (relationship.value === "other" && !relationshipOther.value) return false;
  if (isLegalSurrogate.value === null) return false;
  if (isLegalSurrogate.value === true && !authorityType.value) return false;
  return true;
});

// ---- file handling ----
function triggerFileInput() {
  fileInput.value?.click();
}
function handleFileSelect(e) {
  const f = e.target.files?.[0];
  if (f) {
    method.value = "upload";
    validateAndSet(f);
  }
}
function handleDrop(e) {
  isDragOver.value = false;
  const f = e.dataTransfer?.files?.[0];
  if (f) {
    // A drop on the card selects the upload path implicitly.
    method.value = "upload";
    validateAndSet(f);
  }
}
async function validateAndSet(f) {
  fileError.value = "";
  convertedFile.value = null;
  // Desktop Chrome/Firefox report an empty MIME type for .heic/.heif, so accept
  // those by extension too; the converter decodes them client-side.
  const isHeicByExt = /\.(heic|heif)$/i.test(f.name || "");
  const ok =
    f.type === "application/pdf" || f.type.startsWith("image/") || isHeicByExt;
  if (!ok) {
    fileError.value = "Please upload a PDF or an image file.";
    return;
  }
  // Pre-conversion guard: fail fast on an absurdly large pick before attempting
  // any decode, so a giant HEIC can't hang or OOM the tab.
  if (!isWithinInputLimit(f)) {
    fileError.value = REPICK_MESSAGE;
    return;
  }
  // Submit stays disabled until the conversion resolves.
  file.value = f;
  converting.value = true;
  try {
    convertedFile.value = await convertUploadFile(f);
  } catch (err) {
    // Decode failure, OOM, and WASM-load failure all land here with one message.
    file.value = null;
    convertedFile.value = null;
    fileError.value = REPICK_MESSAGE;
  } finally {
    converting.value = false;
  }
}
function clearFile() {
  file.value = null;
  convertedFile.value = null;
  converting.value = false;
  fileError.value = "";
}
function formatFileSize(bytes) {
  if (!bytes) return "";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

// ---- submit ----
// The stored filename becomes the document's display name everywhere, so a
// phone's "IMG_4021" or "Screenshot_..." name is replaced with something that
// identifies the record: form label (or "Directives"), patient, upload date.
// keyon sanitizes the name on storage (spaces -> underscores); only the
// extension of the converted artifact is kept.
function renamedUploadFile() {
  const f = convertedFile.value;
  if (!f) return f;
  const dot = f.name.lastIndexOf(".");
  const ext = dot > -1 ? f.name.slice(dot) : "";
  const date = new Date().toISOString().slice(0, 10);
  const name =
    [formLabel.value || "Directives", props.patientName, date].filter(Boolean).join(" - ") + ext;
  try {
    return new File([f], name, { type: f.type });
  } catch (e) {
    return f;
  }
}
function submit() {
  const payload = {
    method: method.value,
    relationshipToPatient: relationship.value,
    relationshipOther: relationship.value === "other" ? relationshipOther.value : null,
    isLegalSurrogate: isLegalSurrogate.value === true,
    authorityType: isLegalSurrogate.value === true ? authorityType.value : null,
  };
  if (method.value === "upload") {
    payload.file = renamedUploadFile();
    payload.documentAuthorized = documentAuthorized.value === true;
    payload.formLabel = formLabel.value || null;
  } else {
    payload.directives = manualDirectives.value;
  }
  emit("submit", payload);
}

function onCancel() {
  emit("cancel");
}
</script>

<style scoped>
.dir-modal-overlay {
  /* Backdrop color + z-index from brand-ds (.eh-popup-overlay / --eh-popup-*). */
  position: fixed;
  inset: 0;
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.dir-modal {
  /* Surface, border, radius from brand-ds (.eh-popup-panel / --eh-popup-*). */
  width: 100%;
  max-width: 760px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
.dir-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid var(--eh-divider);
}
/* Patient identity line pinned in the header (name · MRN · DOB). */
.dir-modal-patient {
  margin-top: 4px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--eh-muted);
}
.dir-modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: var(--eh-teal-ink);
  display: flex;
  align-items: center;
  gap: 10px;
}
.dir-close-btn {
  border: none;
  background: transparent;
  font-size: 16px;
  color: var(--eh-muted);
  cursor: pointer;
  /* Real tap target; the negative margin keeps the header height unchanged. */
  width: 44px;
  height: 44px;
  margin: -10px -12px -10px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}
.dir-steps {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--eh-muted-fill);
  border-bottom: 1px solid var(--eh-divider);
  font-size: 13px;
}
.dir-step {
  color: var(--eh-muted);
  font-weight: 600;
}
.dir-step.active {
  color: var(--eh-teal);
}
.dir-step.done {
  color: var(--eh-clinical);
}
.dir-step-sep {
  color: var(--eh-muted-border);
}
.dir-modal-body {
  padding: 20px 24px;
  overflow-y: auto;
}
.dir-section-label {
  font-weight: 600;
  color: var(--eh-teal-ink);
  margin: 0 0 12px;
}
.dir-method-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.dir-method-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 18px 14px;
  border: 2px solid var(--eh-divider);
  border-radius: 10px;
  background: var(--eh-muted-fill);
  cursor: pointer;
  text-align: center;
  transition: all 0.15s;
}
.dir-method-btn:hover {
  border-color: var(--eh-divider);
}
.dir-method-btn.active {
  border-color: var(--eh-teal);
  background: var(--eh-teal-wash);
}
.dir-method-btn .glyphicon {
  font-size: 26px;
  color: var(--eh-teal);
}
.dir-method-btn small {
  color: var(--eh-muted);
  font-size: 12px;
}
/* Upload card doubles as the dropzone. */
.dir-method-drop.drag-over {
  border-color: var(--eh-teal);
  border-style: dashed;
  background: var(--eh-teal-wash);
}
.dir-method-drop .dir-file-types {
  font-size: 11px;
  color: var(--eh-muted);
}
.dir-method-drop .dir-selected-file {
  width: 100%;
}
.dir-subform {
  margin-top: 18px;
  border-top: 1px dashed var(--eh-divider);
  padding-top: 18px;
}
.dir-file-types {
  font-size: 12px;
  color: var(--eh-muted);
}
.dir-selected-file {
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
}
/* Direct child only — the remove button's own glyph must not inherit the
   big green document-icon treatment. */
.dir-selected-file > .glyphicon {
  font-size: 28px;
  color: var(--eh-clinical);
}
.dir-file-details {
  flex: 1;
  min-width: 0;
}
.dir-file-name {
  display: block;
  font-weight: 600;
  color: var(--eh-teal-ink);
  /* Wrap long names inside the card's fixed width instead of stretching it —
     the grid column stays put, the row just grows taller. */
  overflow-wrap: anywhere;
  word-break: break-word;
}
.dir-file-size {
  font-size: 12px;
  color: var(--eh-muted);
}
.dir-processing {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--eh-teal);
  font-weight: 600;
}
.dir-remove-file:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.dir-remove-file {
  border: none;
  background: var(--eh-copper-wash);
  color: var(--eh-copper);
  border-radius: 6px;
  width: 30px;
  height: 30px;
  cursor: pointer;
  flex: none;
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.dir-remove-file .glyphicon {
  font-size: 13px;
  color: inherit;
}
.dir-remove-file:hover:not(:disabled) {
  background: var(--eh-copper);
  color: var(--eh-on-dark);
}
.dir-field {
  margin-top: 16px;
}
.dir-field label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: var(--eh-teal-ink);
  margin-bottom: 6px;
}
.dir-input {
  width: 100%;
  padding: 9px 12px;
  border-radius: 8px;
  font-size: 14px;
}
.dir-hint {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: var(--eh-muted);
}
.dir-yesno {
  display: flex;
  gap: 10px;
}
.dir-toggle {
  flex: 1;
  padding: 9px;
  border: 1px solid var(--eh-divider);
  border-radius: 8px;
  background: var(--eh-white);
  font-weight: 600;
  cursor: pointer;
}
.dir-toggle.active {
  border-color: var(--eh-teal);
  background: var(--eh-teal-wash);
  color: var(--eh-teal);
}
.dir-cat-group {
  margin-bottom: 14px;
}
.dir-cat-label {
  font-weight: 600;
  font-size: 13px;
  color: var(--eh-muted);
  margin-bottom: 6px;
}
.dir-cat-choices {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.dir-choice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid var(--eh-divider);
  border-radius: 8px;
  background: var(--eh-white);
  font-size: 13px;
  cursor: pointer;
}
.dir-choice.active {
  border-color: var(--eh-teal);
  background: var(--eh-teal-wash);
  color: var(--eh-teal);
}
.dir-choice .glyphicon {
  font-size: 13px;
}
.dir-soft-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
  padding: 12px 14px;
  background: color-mix(in srgb, var(--eh-amber) 18%, var(--eh-white));
  border: 1px solid var(--eh-amber);
  border-radius: 8px;
  color: var(--eh-teal-ink);
  font-size: 13px;
}

/* Directive search */
.dir-search {
  position: relative;
}
.dir-search > .glyphicon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--eh-muted);
  font-size: 14px;
}
.dir-search-input {
  padding-left: 36px;
  padding-right: 36px;
}
.dir-search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: var(--eh-muted);
  cursor: pointer;
  font-size: 13px;
}

/* Rich directive cards (code + full name + description) */
.dir-card-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.dir-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 12px 14px;
  border: 1px solid var(--eh-divider);
  border-radius: 8px;
  background: var(--eh-white);
  cursor: pointer;
  transition: all 0.15s;
}
.dir-card:hover {
  border-color: var(--eh-teal);
  background: var(--eh-teal-wash);
}
.dir-card.active {
  border-color: var(--eh-teal);
  background: var(--eh-teal-wash);
}
.dir-card-check {
  margin-top: 2px;
  font-size: 15px;
  color: var(--eh-teal);
  flex-shrink: 0;
}
.dir-card-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.dir-card-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}
.dir-card-head strong {
  color: var(--eh-teal-ink);
  font-size: 14px;
}
.dir-card-code {
  font-size: 11px;
  font-weight: 700;
  color: var(--eh-muted);
  background: var(--eh-muted-fill);
  padding: 1px 7px;
  border-radius: 999px;
  white-space: nowrap;
}
.dir-card-desc {
  font-size: 12.5px;
  color: var(--eh-muted);
  line-height: 1.4;
}

/* Selected summary */
.dir-selected-box {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
  padding: 12px 14px;
  background: var(--eh-muted-fill);
  border: 1px solid var(--eh-divider);
  border-radius: 8px;
}
.dir-selected-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--eh-muted);
}
.dir-pill-selected {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  background: var(--eh-teal-wash);
  color: var(--eh-teal);
  border: 1px solid var(--eh-teal);
  padding: 3px 8px;
  border-radius: 999px;
}
.dir-pill-selected .glyphicon {
  cursor: pointer;
  font-size: 10px;
}
.dir-soft-warning .glyphicon {
  margin-top: 1px;
}
.dir-inline-error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 10px 12px;
  background: var(--eh-copper-wash);
  border-radius: 8px;
  color: var(--eh-copper);
  font-size: 13px;
}
/* Attestation dropdowns (relationship / authority). `appearance: none` plus a
   drawn caret so the control matches .dir-input across browsers; 44px high so
   it is a real tap target. */
.dir-select {
  appearance: none;
  -webkit-appearance: none;
  min-height: 44px;
  padding-right: 38px;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%234a5568' stroke-width='1.8' d='M1 1.5 6 6.5l5-5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  cursor: pointer;
}
.dir-modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--eh-divider);
}
.dir-footer-left {
  display: flex;
}
.dir-footer-right {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dir-result-preview {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  color: var(--eh-muted);
}
.dir-result-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--eh-muted-fill);
  border: 1px solid var(--eh-divider);
  font-size: 12px;
  font-weight: 600;
  color: var(--eh-teal-ink);
}
.dir-result-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: none;
}
.dir-btn-secondary,
.dir-btn-primary {
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}
.dir-btn-primary {
  border: none;
}
.dir-btn-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.dir-spin {
  animation: dir-spin 1s linear infinite;
}
@keyframes dir-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (max-width: 600px) {
  .dir-method-grid {
    grid-template-columns: 1fr;
  }
  /* Full-screen edge-to-edge on phones, minus the app's fixed Messages bar.
     That bar is pinned at z-index 2147483647 (max int), so no overlay can
     stack above it — reserve its 48px or it eats the footer's buttons. */
  .dir-modal-overlay {
    padding: 0 0 48px;
  }
  .dir-modal {
    max-width: none;
    width: 100%;
    height: 100%;
    max-height: calc(100dvh - 48px);
    border-radius: 0;
  }
  /* The footer laid the status preview, Cancel and Submit out in one unwrapped
     row, which pushed Submit off the right edge — untappable on a phone. Stack
     it instead. `display: contents` dissolves the two side wrappers so all four
     children can be ordered against each other: outcome preview, primary
     action, then the two ways back. */
  .dir-modal-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 12px 16px;
  }
  .dir-footer-left,
  .dir-footer-right {
    display: contents;
  }
  .dir-modal-footer button {
    width: 100%;
    min-height: 44px;
    justify-content: center;
  }
  .dir-result-preview {
    order: 1;
    justify-content: center;
  }
  .dir-btn-primary {
    order: 2;
  }
  .dir-footer-left > button {
    order: 3;
  }
  .dir-btn-secondary[data-cy="modal-cancel"] {
    order: 4;
  }
  /* Tighter method cards so both fit above the fold on a phone. The
     descriptions stay — they are how a first-time submitter picks correctly. */
  .dir-method-btn {
    padding: 12px 14px;
    gap: 4px;
  }
}
</style>
